/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Button } from 'antd'
import { resendVerificationEmail } from 'api/auth'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ZentakeContext } from 'utils/ZentakeContext'

import { EmailSent } from '@/components/Svg/EmailSent'
import { Waves } from '@/components/Svg/Waves'
import { ZentakeLogo } from '@/components/Svg/ZentakeLogo'
import { UserDetail } from '@/types/user'
import { ZentakeContextType } from '@/types/zentake_context'

const RESEND_VERIFICATION_EMAIL_INTERVAL = 60

let interval: NodeJS.Timeout | null = null

type ContextProps = ZentakeContextType & {
  setUserDetails: React.Dispatch<React.SetStateAction<UserDetail | null>>
}

export const VerifyEmail = () => {
  const [isLoading, setIsLoading] = React.useState(false)
  const {
    userDetails,
    fetchingUserDetails,
    setGlobalLoading,
    updateUserDetails,
  } = React.useContext(ZentakeContext) as unknown as ContextProps
  const [secondsUntilResend, setSecondsUntilResend] = React.useState(0)
  const history = useHistory()

  React.useEffect(() => {
    if (userDetails && userDetails?.is_verified) {
      history.push('/')
    }
  }, [history, userDetails])

  React.useEffect(() => {
    // compares the current time with userDetails.last_email_verification_sent
    // if the difference is of 3 minutes or less, we don't allow the user to resend the email
    if (!userDetails?.last_email_verification_sent) return

    const lastEmailVerificationSent = new Date(
      userDetails?.last_email_verification_sent + '+0000'
    )

    const currentTime = new Date()
    const difference =
      currentTime.getTime() - lastEmailVerificationSent.getTime()

    const secondsDifference = Math.floor(difference / 1000)

    if (secondsDifference < RESEND_VERIFICATION_EMAIL_INTERVAL) {
      setSecondsUntilResend(
        RESEND_VERIFICATION_EMAIL_INTERVAL - secondsDifference
      )

      if (interval) {
        clearInterval(interval)
      }

      interval = setInterval(() => {
        setSecondsUntilResend((seconds) => seconds - 1)
      }, 1000)

      return
    }

    if (interval) {
      clearInterval(interval)
    }
  }, [userDetails])

  const onResendVerificationEmail = React.useCallback(async () => {
    if (!userDetails?.email) return
    setIsLoading(true)
    try {
      await resendVerificationEmail(userDetails.email)

      await updateUserDetails()

      toast.success('Verification email sent')
    } catch {
      toast.error('Failed to send verification email')
    } finally {
      setIsLoading(false)
    }
  }, [userDetails?.email, updateUserDetails])

  React.useEffect(() => {
    if (
      !fetchingUserDetails ||
      userDetails?.email ||
      !userDetails?.is_verified
    ) {
      setGlobalLoading(false)
    }
  }, [
    fetchingUserDetails,
    setGlobalLoading,
    userDetails?.email,
    userDetails?.is_verified,
  ])

  if (fetchingUserDetails || !userDetails?.email || userDetails?.is_verified) {
    return null
  }
  return (
    <div className="relative flex h-full w-full flex-col bg-white">
      <Waves className="absolute left-0 top-0 z-[1]" />
      <div className="relative z-[2] flex h-full flex-col items-center justify-start pt-20">
        <EmailSent width={360} />
        <p className="mt-6 text-base">We sent an email to</p>
        <p className="text-base font-bold">{userDetails.email}</p>
        <p className="mt-6 text-center text-base">
          <strong>Check your inbox</strong> to activate your <br /> account.
        </p>
        <Button
          className="my-8"
          disabled={isLoading || Boolean(secondsUntilResend > 0)}
          loading={isLoading}
          onClick={onResendVerificationEmail}
        >
          {secondsUntilResend > 0 ? (
            <>Resend in {secondsUntilResend} seconds</>
          ) : (
            'Resend email'
          )}
        </Button>
        <p className="text-center text-sm text-gray-500">
          Not getting the email? <br />
          Check your spam/junk folder
        </p>
      </div>
      <footer className="flex w-full flex-col items-center justify-center gap-2.5 bg-white shadow-none">
        <div className="flex items-center justify-center">
          <ZentakeLogo width={150} />
        </div>
        <p className="w-full text-center text-xs text-[#6288F6]">
          Copyright © 2022 ZENTAKE Inc.
        </p>
      </footer>
    </div>
  )
}
